import { html, render, nothing } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map.js';
import { styleMap } from 'lit-html/directives/style-map.js';
import { GestureController } from './gestures/index.js';

/**
 * CardStack - A reusable card navigation component with gesture support
 *
 * Features:
 * - Vertical stack navigation (swipe up/down)
 * - Horizontal actions (swipe left/right) with configurable behavior per card
 * - Touch, mouse, and trackpad support
 * - Overlay feedback during gestures
 * - Side panel support for additional actions
 * - Queue jumping (skip to next actionable card)
 *
 * @example
 * const stack = new CardStack(document.getElementById('app'), {
 *   items: [...],
 *   renderCard: (item, index, state) => html`<div>${item.title}</div>`,
 *   getCardConfig: (item, index) => ({
 *     swipeLeft: 'decline',
 *     swipeRight: 'approve',
 *     background: item.bg,
 *   }),
 * });
 *
 * stack.on('swipe-right', ({ item, index }) => console.log('Approved', item));
 */
export class CardStack {
  constructor(container, options = {}) {
    this.container = container;

    // Configuration
    this.threshold = options.threshold ?? 100;
    this.animationDuration = options.animationDuration ?? 300;
    this.minScale = options.minScale ?? 0.85;

    // Required: render function for card content
    this.renderCard = options.renderCard ?? (() => html`<div>No content</div>`);

    // Optional: per-card configuration
    this.getCardConfig = options.getCardConfig ?? (() => ({}));

    // Optional: overlay templates
    this.renderLeftOverlay = options.renderLeftOverlay ?? null;
    this.renderRightOverlay = options.renderRightOverlay ?? null;

    // Optional: side panel template (shown on left swipe)
    this.renderSidePanel = options.renderSidePanel ?? null;

    // Optional: side panel class getter (e.g., 'decline' or 'kill')
    this.getSidePanelClass = options.getSidePanelClass ?? (() => '');

    // Optional: right side panel template (shown on right swipe)
    this.renderRightSidePanel = options.renderRightSidePanel ?? null;

    // Optional: right side panel class getter
    this.getRightSidePanelClass = options.getRightSidePanelClass ?? (() => '');

    // Optional: find next target card for queue jumping
    this.findNextTarget = options.findNextTarget ?? null;

    // Optional: nested scrollable element detector
    this.isNestedScrollable = options.isNestedScrollable ?? ((target) => target.closest('[data-scroll]'));

    // State
    this.items = options.items ?? [];
    this.current = options.initialIndex ?? 0;
    this.cardElements = [];
    this.nextTargetIndex = -1;

    // Gesture state
    this.isAnimating = false;
    this.isSidePanelOpen = false;

    // Event listeners
    this.listeners = new Map();

    // Keyboard support (enabled by default, disable with keyboard: false)
    this.keyboardEnabled = options.keyboard !== false;

    // Initialize
    this.setupContainer();
    this.gestureController = this.createGestureController();
    this.render();

    if (this.keyboardEnabled) {
      this._onKeyDown = (e) => this.handleKeyDown(e);
      document.addEventListener('keydown', this._onKeyDown);
    }
  }

  // ============================================================
  // Public API
  // ============================================================

  /**
   * Set items and re-render
   */
  setItems(items) {
    this.items = items;
    this.current = Math.min(this.current, items.length - 1);
    this.render();
  }

  /**
   * Update a single item and re-render
   */
  updateItem(index, newData) {
    if (index < 0 || index >= this.items.length) return;
    this.items[index] = { ...this.items[index], ...newData };
    // Re-render all cards - lit-html will efficiently diff and only update what changed
    this.renderCards();
  }

  /**
   * Navigate to specific index
   */
  goTo(index, animate = true) {
    if (index < 0 || index >= this.items.length) return;
    if (index === this.current) return;

    const direction = index > this.current ? 'up' : 'down';
    const from = this.current;
    this.current = index;

    if (animate) {
      this.animateNavigation(direction);
    } else {
      this.positionAllCards();
    }

    this.emit('navigate', { from, to: index, direction });
  }

  /**
   * Navigate to next card
   */
  next() {
    if (this.current < this.items.length - 1) {
      this.goTo(this.current + 1);
    }
  }

  /**
   * Navigate to previous card
   */
  prev() {
    if (this.current > 0) {
      this.goTo(this.current - 1);
    }
  }

  /**
   * Get current index
   */
  getCurrentIndex() {
    return this.current;
  }

  /**
   * Get current item
   */
  getCurrentItem() {
    return this.items[this.current];
  }

  /**
   * Find the index of an item in the items array
   */
  findItemIndex(item) {
    return this.items.indexOf(item);
  }

  /**
   * Lock/unlock gestures (e.g., when modal is open)
   */
  setLocked(locked) {
    this.gestureController.setLocked(locked);
  }

  /**
   * Close side panel if open
   */
  closeSidePanel() {
    if (!this.isSidePanelOpen) return;
    this.isSidePanelOpen = false;
    this._rightSidePanelOpen = false;
    this.animateSnapBack();
    this.emit('side-panel-close', { index: this.current });
  }

  /**
   * Register event listener
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    return () => this.off(event, callback);
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) callbacks.splice(index, 1);
    }
  }

  /**
   * Clean up
   */
  destroy() {
    this.gestureController.destroy();
    if (this._onKeyDown) {
      document.removeEventListener('keydown', this._onKeyDown);
    }
    this.listeners.clear();
    this.container.innerHTML = '';
  }

  // ============================================================
  // Internal: Keyboard Navigation
  // ============================================================

  handleKeyDown(e) {
    if (this.isAnimating || this.gestureController.locked) return;

    // Escape closes side panel
    if (e.key === 'Escape' && this.isSidePanelOpen) {
      this.closeSidePanel();
      return;
    }

    // When side panel is open, only allow closing it
    if (this.isSidePanelOpen) {
      const closeKey = this._rightSidePanelOpen ? 'ArrowRight' : 'ArrowLeft';
      if (e.key === closeKey) {
        e.preventDefault();
        this.closeSidePanel();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        if (this.current < this.items.length - 1) {
          e.preventDefault();
          this.next();
        }
        break;

      case 'ArrowUp':
        if (this.current > 0) {
          e.preventDefault();
          this.prev();
        }
        break;

      case 'ArrowLeft':
        e.preventDefault();
        this.triggerSwipeRight();
        break;

      case 'ArrowRight':
        e.preventDefault();
        this.triggerSwipeLeft();
        break;
    }
  }

  triggerSwipeRight() {
    const item = this.items[this.current];
    const config = this.getCardConfig(item, this.current);
    if (config.swipeRight === false) return;

    const card = this.cardElements[this.current];
    if (!card) return;

    this.isAnimating = true;

    // Flash overlay
    const overlay = card.querySelector('.card-stack-overlay-right');
    if (overlay) overlay.style.opacity = '1';

    setTimeout(() => {
      if (this.renderRightSidePanel && config.rightPanel) {
        // Right side panel mode
        this.resetOverlays(card, true);
        card.classList.add('animating');
        const rightPanel = card.querySelector('.card-stack-right-side-panel');
        if (rightPanel) rightPanel.style.visibility = 'visible';

        card.style.transform = `translateX(${this.container.offsetWidth}px)`;

        setTimeout(() => {
          this.isAnimating = false;
          this.isSidePanelOpen = true;
          this._rightSidePanelOpen = true;

          this.emit('swipe-right', {
            item,
            index: this.current,
            action: config.swipeRight,
            panel: rightPanel,
          });
        }, this.animationDuration);
      } else {
        const nextIdx = this.findNextTarget?.(this.items, this.current) ?? -1;
        if (nextIdx !== -1 && nextIdx !== this.current) {
          // Has next target: dismiss card and jump
          this.resetOverlays(card, true);
          card.classList.add('animating');
          card.style.transform = 'translateX(100vw) rotate(20deg)';

          this.emit('swipe-right', {
            item,
            index: this.current,
            action: config.swipeRight,
          });

          this.nextTargetIndex = nextIdx;
          this.transitionToNextTarget();
        } else {
          // Ghost mode: overlay is already at opacity 1, slide it off-screen
          this.animateGhostSwipe(card, 'right');

          this.emit('swipe-right', {
            item,
            index: this.current,
            action: config.swipeRight,
          });

          setTimeout(() => {
            this.isAnimating = false;
            this.resetGhostSwipe(card, 'right');
            this.updateZIndexes();
          }, this.animationDuration);
        }
      }
    }, 150);
  }

  triggerSwipeLeft() {
    const item = this.items[this.current];
    const config = this.getCardConfig(item, this.current);
    if (config.swipeLeft === false) return;

    const card = this.cardElements[this.current];
    if (!card) return;

    this.isAnimating = true;

    // Flash overlay
    const overlay = card.querySelector('.card-stack-overlay-left');
    if (overlay) overlay.style.opacity = '1';

    setTimeout(() => {
      if (!this.renderSidePanel) {
        // No side panel — dismiss like swipe-right
        const nextIdx = this.findNextTarget?.(this.items, this.current) ?? -1;
        if (nextIdx !== -1 && nextIdx !== this.current) {
          // Has next target: dismiss card and jump
          this.resetOverlays(card, true);
          card.classList.add('animating');
          card.style.transform = 'translateX(-100vw) rotate(-20deg)';

          this.emit('swipe-left', {
            item,
            index: this.current,
            action: config.swipeLeft,
          });

          this.nextTargetIndex = nextIdx;
          this.transitionToNextTarget();
        } else {
          // Ghost mode: overlay is already at opacity 1, slide it off-screen
          this.animateGhostSwipe(card, 'left');

          this.emit('swipe-left', {
            item,
            index: this.current,
            action: config.swipeLeft,
          });

          setTimeout(() => {
            this.isAnimating = false;
            this.resetGhostSwipe(card, 'left');
            this.updateZIndexes();
          }, this.animationDuration);
        }
      } else {
        // Side panel mode
        this.resetOverlays(card, true);
        card.classList.add('animating');
        const sidePanel = card.querySelector('.card-stack-side-panel');
        if (sidePanel) sidePanel.style.visibility = 'visible';

        card.style.transform = `translateX(${-this.container.offsetWidth}px)`;

        setTimeout(() => {
          this.isAnimating = false;
          this.isSidePanelOpen = true;

          this.emit('swipe-left', {
            item,
            index: this.current,
            action: config.swipeLeft,
            panel: sidePanel,
          });
        }, this.animationDuration);
      }
    }, 150);
  }

  // ============================================================
  // Internal: Setup
  // ============================================================

  setupContainer() {
    this.container.classList.add('card-stack');
    this.container.style.cssText = `
      position: relative;
      overflow: hidden;
      width: 100%;
      height: 100%;
    `;
  }

  createGestureController() {
    return new GestureController(this.container, {
      threshold: this.threshold,
      isNestedScrollable: this.isNestedScrollable,

      shouldLockDirection: (direction) => {
        // Allow consumer to restrict horizontal swipes
        if (direction === 'h') {
          const config = this.getCardConfig(this.items[this.current], this.current);
          return config.swipeLeft !== false || config.swipeRight !== false;
        }
        return true;
      },

      onGestureStart: () => {
        if (this.isAnimating) return;
        if (!this.isSidePanelOpen) {
          this.removeAnimatingClass();
          this.nextTargetIndex = -1;
        }
      },

      onGestureMove: ({ deltaX, deltaY, direction, progress }) => {
        if (this.isAnimating) return;
        if (this.isSidePanelOpen) {
          // Only allow dragging back toward center
          if (direction === 'h') {
            const returning = this._rightSidePanelOpen ? deltaX < 0 : deltaX > 0;
            if (returning) {
              const card = this.cardElements[this.current];
              if (!card) return;
              const base = this._rightSidePanelOpen
                ? this.container.offsetWidth
                : -this.container.offsetWidth;
              card.style.transform = `translateX(${base + deltaX}px)`;
            }
          }
          return;
        }
        this.handleGestureMove(deltaX, deltaY, direction, progress);
      },

      onGestureEnd: ({ deltaX, deltaY, direction, action }) => {
        if (this.isAnimating) return;
        if (this.isSidePanelOpen) {
          // Close if swiped back past threshold
          if (direction === 'h') {
            const returning = this._rightSidePanelOpen
              ? (action === 'swipe-left')
              : (action === 'swipe-right');
            if (returning) {
              this.closeSidePanel();
            } else {
              // Snap back to panel-open position
              const card = this.cardElements[this.current];
              if (card) {
                card.classList.add('animating');
                const base = this._rightSidePanelOpen
                  ? this.container.offsetWidth
                  : -this.container.offsetWidth;
                card.style.transform = `translateX(${base}px)`;
                setTimeout(() => card.classList.remove('animating'), this.animationDuration);
              }
            }
          }
          return;
        }
        this.handleGestureEnd(deltaX, deltaY, direction, action);
      },
    });
  }

  // ============================================================
  // Internal: Gesture Handling
  // ============================================================

  handleGestureMove(deltaX, deltaY, direction, progress) {
    const card = this.cardElements[this.current];
    if (!card) return;

    const config = this.getCardConfig(this.items[this.current], this.current);

    if (direction === 'h') {
      this.handleHorizontalMove(card, deltaX, config);
    } else if (direction === 'v') {
      this.handleVerticalMove(deltaY);
    }
  }

  handleHorizontalMove(card, deltaX, config) {
    // Right swipe (positive deltaX)
    if (deltaX > 0 && config.swipeRight !== false) {
      // Show right side panel if this card has one
      const hasRightPanel = this.renderRightSidePanel && config.rightPanel;

      // Prepare next target card (queue jumping) - only when no right side panel
      if (!hasRightPanel) {
        this.prepareNextTarget(deltaX);
      }

      // Determine if we have a valid next target
      const hasNextTarget = !hasRightPanel && this.nextTargetIndex !== -1 && this.nextTargetIndex !== this.current;

      // Only move the card if there's a next target or a side panel to reveal
      if (hasNextTarget || hasRightPanel) {
        card.style.transform = `translateX(${deltaX}px) rotate(${deltaX * 0.02}deg)`;
      }
      // Ghost mode: card stays in place, only overlay shows

      // Update approve overlay
      const overlay = card.querySelector('.card-stack-overlay-right');
      if (overlay) {
        overlay.style.opacity = Math.max(0, deltaX / this.threshold);
      }

      if (hasRightPanel) {
        const rightPanel = card.querySelector('.card-stack-right-side-panel');
        if (rightPanel) {
          rightPanel.style.visibility = 'visible';
        }
      }
    }
    // Left swipe (negative deltaX)
    else if (deltaX < 0) {
      const canSwipeLeft = config.swipeLeft !== false;

      // Prepare next target card for queue jumping (when no side panel)
      if (!this.renderSidePanel && canSwipeLeft) {
        this.prepareNextTarget(-deltaX);
      }

      // Determine if we have a valid next target
      const hasNextTarget = !this.renderSidePanel && canSwipeLeft && this.nextTargetIndex !== -1 && this.nextTargetIndex !== this.current;

      // Only move the card if there's a next target or a side panel to reveal
      if (hasNextTarget || this.renderSidePanel) {
        const clampedDelta = canSwipeLeft ? deltaX : Math.max(deltaX, -50); // Resist if disabled
        card.style.transform = `translateX(${clampedDelta}px) rotate(${clampedDelta * 0.02}deg)`;
      } else if (!canSwipeLeft) {
        // Resist if disabled
        const clampedDelta = Math.max(deltaX, -50);
        card.style.transform = `translateX(${clampedDelta}px) rotate(${clampedDelta * 0.02}deg)`;
      }
      // Ghost mode: card stays in place, only overlay shows

      // Update reject overlay
      const overlay = card.querySelector('.card-stack-overlay-left');
      if (overlay) {
        overlay.style.opacity = Math.min(1, -deltaX / this.threshold);
      }

      // Show side panel (only if renderSidePanel is provided)
      if (this.renderSidePanel) {
        const sidePanel = card.querySelector('.card-stack-side-panel');
        if (sidePanel && canSwipeLeft) {
          sidePanel.style.visibility = 'visible';
        }
      }
    }
  }

  handleVerticalMove(deltaY) {
    const dragPercent = (deltaY / window.innerHeight) * 100;
    this.positionAllCards(dragPercent);
  }

  prepareNextTarget(deltaX) {
    // Don't apply scale to the current card being swiped
    if (this.nextTargetIndex !== -1 && this.nextTargetIndex !== this.current) {
      // Already prepared, update scale
      const progress = Math.min(1, deltaX / (this.container.offsetWidth * 0.5));
      const scale = this.minScale + (1 - this.minScale) * progress;
      this.cardElements[this.nextTargetIndex].style.transform = `scale(${scale})`;
      return;
    }

    // Find next target
    if (this.findNextTarget) {
      this.nextTargetIndex = this.findNextTarget(this.items, this.current);

      if (this.nextTargetIndex !== -1 && this.nextTargetIndex !== this.current) {
        this.updateZIndexes(this.current, this.nextTargetIndex);
        this.cardElements[this.nextTargetIndex].style.transform = `scale(${this.minScale})`;
      }
    }
  }

  handleGestureEnd(deltaX, deltaY, direction, action) {
    const card = this.cardElements[this.current];
    const item = this.items[this.current];
    const config = this.getCardConfig(item, this.current);

    if (direction === 'h') {
      this.handleHorizontalEnd(card, item, config, deltaX, action);
    } else if (direction === 'v') {
      this.handleVerticalEnd(deltaY, action);
    } else {
      this.animateSnapBack();
    }
  }

  handleHorizontalEnd(card, item, config, deltaX, action) {
    if (action === 'swipe-right' && config.swipeRight !== false) {
      // Swipe right action
      this.isAnimating = true;

      // If right side panel is provided for this card, open it instead of dismissing
      if (this.renderRightSidePanel && config.rightPanel) {
        this.resetOverlays(card, true);
        card.classList.add('animating');
        card.style.transform = `translateX(${this.container.offsetWidth}px)`;

        setTimeout(() => {
          this.isAnimating = false;
          this.isSidePanelOpen = true;
          this._rightSidePanelOpen = true;

          this.emit('swipe-right', {
            item,
            index: this.current,
            action: config.swipeRight,
            panel: card.querySelector('.card-stack-right-side-panel'),
          });
        }, this.animationDuration);
      } else if (this.nextTargetIndex !== -1 && this.nextTargetIndex !== this.current) {
        // Has next target: dismiss card and jump
        this.resetOverlays(card, true);
        card.classList.add('animating');
        card.style.transform = 'translateX(100vw) rotate(20deg)';

        this.emit('swipe-right', {
          item,
          index: this.current,
          action: config.swipeRight,
        });

        this.transitionToNextTarget();
      } else {
        // Ghost mode: no next target, animate overlay off-screen
        this.animateGhostSwipe(card, 'right');

        this.emit('swipe-right', {
          item,
          index: this.current,
          action: config.swipeRight,
        });

        setTimeout(() => {
          this.isAnimating = false;
          this.resetGhostSwipe(card, 'right');
          this.updateZIndexes();
        }, this.animationDuration);
      }
    } else if (action === 'swipe-left' && config.swipeLeft !== false) {
      // Swipe left action
      this.isAnimating = true;

      // If no side panel, behave like swipe-right (dismiss and move to next)
      if (!this.renderSidePanel) {
        if (this.nextTargetIndex !== -1 && this.nextTargetIndex !== this.current) {
          // Has next target: dismiss card and jump
          this.resetOverlays(card, true);
          card.classList.add('animating');
          card.style.transform = `translateX(-100vw) rotate(-20deg)`;

          this.emit('swipe-left', {
            item,
            index: this.current,
            action: config.swipeLeft,
          });

          this.transitionToNextTarget();
        } else {
          // Ghost mode: no next target, animate overlay off-screen
          this.animateGhostSwipe(card, 'left');

          this.emit('swipe-left', {
            item,
            index: this.current,
            action: config.swipeLeft,
          });

          setTimeout(() => {
            this.isAnimating = false;
            this.resetGhostSwipe(card, 'left');
            this.updateZIndexes();
          }, this.animationDuration);
        }
      } else {
        // Side panel mode - slide card off and open panel
        this.resetOverlays(card, true);
        card.classList.add('animating');
        card.style.transform = `translateX(${-this.container.offsetWidth}px)`;

        setTimeout(() => {
          this.isAnimating = false;
          this.isSidePanelOpen = true;

          this.emit('swipe-left', {
            item,
            index: this.current,
            action: config.swipeLeft,
            panel: card.querySelector('.card-stack-side-panel'),
          });
        }, this.animationDuration);
      }
    } else {
      // Snap back
      this.animateSnapBack();
    }
  }

  handleVerticalEnd(deltaY, action) {
    if (action === 'swipe-up' && this.current < this.items.length - 1) {
      const from = this.current;
      this.current++;
      this.animateNavigation('up');
      this.emit('navigate', { from, to: this.current, direction: 'up' });
    } else if (action === 'swipe-down' && this.current > 0) {
      const from = this.current;
      this.current--;
      this.animateNavigation('down');
      this.emit('navigate', { from, to: this.current, direction: 'down' });
    } else {
      this.animateSnapBack();
    }
  }

  transitionToNextTarget() {
    const oldCurrent = this.current;
    this.current = this.nextTargetIndex;
    this.nextTargetIndex = -1;

    // Emit navigate event for queue jumping
    this.emit('navigate', { from: oldCurrent, to: this.current, direction: 'jump' });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.cardElements[this.current].classList.add('animating');
        this.cardElements[this.current].style.transform = 'scale(1)';
        this.updateZIndexes();

        setTimeout(() => {
          this.isAnimating = false;
          this.cardElements[oldCurrent]?.classList.remove('animating');
          // Position all cards EXCEPT the one that was just swiped away
          // It should stay off-screen until its content is fully updated
          this.positionAllCardsExcept(oldCurrent);
          this.updateZIndexes();
        }, this.animationDuration);
      });
    });
  }

  // ============================================================
  // Internal: Animations
  // ============================================================

  animateNavigation(direction) {
    // Cancel any pending snap-back cleanup
    clearTimeout(this._snapBackTimer);
    this._snapBackTimer = null;

    this.isAnimating = true;
    this.addAnimatingClass();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.positionAllCards();
        this.updateZIndexes();

        setTimeout(() => {
          this.isAnimating = false;
          this.removeAnimatingClass();
        }, this.animationDuration);
      });
    });
  }

  animateGhostSwipe(card, direction) {
    const overlayClass = direction === 'right' ? '.card-stack-overlay-right' : '.card-stack-overlay-left';
    const overlay = card.querySelector(overlayClass);
    if (overlay) {
      overlay.classList.add('animating');
      overlay.style.opacity = '1';
      overlay.style.transform = direction === 'right' ? 'translateX(100vw)' : 'translateX(-100vw)';
    }
  }

  resetGhostSwipe(card, direction) {
    const overlayClass = direction === 'right' ? '.card-stack-overlay-right' : '.card-stack-overlay-left';
    const overlay = card.querySelector(overlayClass);
    if (overlay) {
      overlay.classList.remove('animating');
      overlay.style.opacity = '0';
      overlay.style.transform = '';
    }
    this.nextTargetIndex = -1;
  }

  animateSnapBack() {
    const card = this.cardElements[this.current];
    if (!card) return;

    this.resetOverlays(card, true);
    card.classList.add('animating');
    card.style.transform = 'translateX(0) rotate(0deg)';

    // Reset next target card
    if (this.nextTargetIndex !== -1 && this.nextTargetIndex !== this.current) {
      const nextCard = this.cardElements[this.nextTargetIndex];
      nextCard.classList.add('animating');
      nextCard.style.transform = `translateY(${(this.nextTargetIndex - this.current) * 100}%)`;
    }

    // Hide side panels
    setTimeout(() => {
      const sidePanel = card.querySelector('.card-stack-side-panel');
      if (sidePanel) sidePanel.style.visibility = 'hidden';
      const rightPanel = card.querySelector('.card-stack-right-side-panel');
      if (rightPanel) rightPanel.style.visibility = 'hidden';
      this.updateZIndexes();
    }, this.animationDuration);

    this.addAnimatingClass();
    this.positionAllCards();

    clearTimeout(this._snapBackTimer);
    this._snapBackTimer = setTimeout(() => {
      this._snapBackTimer = null;
      if (!this.isAnimating) {
        this.removeAnimatingClass();
      }
    }, this.animationDuration);

    this.nextTargetIndex = -1;
  }

  addAnimatingClass() {
    this.cardElements.forEach(card => card?.classList.add('animating'));
  }

  removeAnimatingClass() {
    this.cardElements.forEach(card => card?.classList.remove('animating'));
  }

  // ============================================================
  // Internal: Positioning
  // ============================================================

  positionAllCards(offsetPercent = 0) {
    this.cardElements.forEach((card, index) => {
      if (!card) return;
      const pos = (index - this.current) * 100 + offsetPercent;
      card.style.transform = `translateY(${pos}%)`;
    });
  }

  positionAllCardsExcept(excludeIndex, offsetPercent = 0) {
    this.cardElements.forEach((card, index) => {
      if (!card || index === excludeIndex) return;
      const pos = (index - this.current) * 100 + offsetPercent;
      card.style.transform = `translateY(${pos}%)`;
    });
  }

  updateZIndexes(activeIdx = this.current, behindIdx = -1) {
    this.cardElements.forEach((card, i) => {
      if (!card) return;
      card.style.zIndex = '1';
    });

    if (activeIdx >= 0 && activeIdx < this.cardElements.length) {
      this.cardElements[activeIdx].style.zIndex = '10';
    }

    if (behindIdx >= 0 && behindIdx < this.cardElements.length) {
      this.cardElements[behindIdx].style.zIndex = '5';
    }
  }

  resetOverlays(card, animate = false) {
    const leftOverlay = card.querySelector('.card-stack-overlay-left');
    const rightOverlay = card.querySelector('.card-stack-overlay-right');

    if (animate) {
      leftOverlay?.classList.add('animating');
      rightOverlay?.classList.add('animating');
    }

    if (leftOverlay) leftOverlay.style.opacity = 0;
    if (rightOverlay) rightOverlay.style.opacity = 0;

    if (animate) {
      setTimeout(() => {
        leftOverlay?.classList.remove('animating');
        rightOverlay?.classList.remove('animating');
      }, this.animationDuration);
    }
  }

  // ============================================================
  // Internal: Rendering
  // ============================================================

  render() {
    this.renderCards();

    // Cache card elements
    this.cardElements = Array.from(this.container.querySelectorAll('.card-stack-card'));

    // Initial positioning
    this.positionAllCards();
    this.updateZIndexes();
  }

  renderCards() {
    const template = html`
      ${this.items.map((item, index) => this.renderCardWrapper(item, index))}
    `;

    render(template, this.container);
  }

  renderCardWrapper(item, index) {
    const config = this.getCardConfig(item, index);
    const isActive = index === this.current;

    const styles = {
      background: config.background || 'var(--card-bg, #1a1a2e)',
    };

    const sidePanelClass = this.getSidePanelClass(item, index);

    return html`
      <div
        class="card-stack-card"
        style=${styleMap(styles)}
        data-index=${index}
      >
        ${this.renderLeftOverlay ? html`
          <div class="card-stack-overlay card-stack-overlay-left">
            ${this.renderLeftOverlay(item, index)}
          </div>
        ` : nothing}
        ${this.renderRightOverlay ? html`
          <div class="card-stack-overlay card-stack-overlay-right">
            ${this.renderRightOverlay(item, index)}
          </div>
        ` : nothing}
        ${this.renderSidePanel && config.swipeLeft !== false ? html`
          <div class="card-stack-side-panel ${sidePanelClass}">
            ${this.renderSidePanel(item, index, {
              close: () => this.closeSidePanel(),
              confirm: (data) => this.handleSidePanelConfirm(item, index, data),
            })}
          </div>
        ` : nothing}
        ${this.renderRightSidePanel && config.rightPanel ? html`
          <div class="card-stack-right-side-panel ${this.getRightSidePanelClass(item, index)}">
            ${this.renderRightSidePanel(item, index, {
              close: () => this.closeSidePanel(),
              confirm: (data) => this.handleRightSidePanelConfirm(item, index, data),
            })}
          </div>
        ` : nothing}
        <div class="card-stack-card-content">
          ${this.renderCard(item, index, { isActive })}
        </div>
      </div>
    `;
  }

  handleSidePanelConfirm(item, index, data) {
    this.emit('side-panel-confirm', { item, index, data });
    this.closeSidePanel();

    // Navigate to next target if available
    if (this.findNextTarget) {
      const nextIdx = this.findNextTarget(this.items, this.current);
      if (nextIdx !== -1 && nextIdx !== this.current) {
        this.goTo(nextIdx);
      }
    }
  }

  handleRightSidePanelConfirm(item, index, data) {
    this.emit('right-side-panel-confirm', { item, index, data });
    this.closeSidePanel();
  }

  // ============================================================
  // Internal: Events
  // ============================================================

  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(cb => cb(data));
    }
  }
}
